import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";

import { NextUp } from "@/components/Callouts";

export default function EarTraining3() {
  const { lesson, course, accent } = findLesson("ear-training", "3");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET3-Feel-Time-Animated.mp4"
        caption="Pulse + subdivisions (2 · 3 · 4 per beat) on a shared timeline"
        hint="≈ 28 sec · watch first"
      />
      <Chapter number="03" eyebrow="Ear Training · Chapter Three" title="Hearing time itself: ÷2, ÷3, or ÷4?">
        <Prose>
          <p>
            Ear training isn't only about melody and chords. <strong>Rhythm needs training
            too</strong> — and it's the part people tend to forget. Yet identifying how a song
            divides its beat is one of the most useful ear-training skills you can pick up. It tells you whether to count "1 and 2 and", "1 and-a 2 and-a", or
            "1 e and a 2 e and a" before you've even decided what notes to play.
          </p>
          <p>
            The rule is simple: every beat in popular music is divided into either 2, 3, or
            4 equal parts. There are no other common divisions. So when you hear a song,
            ask one question: <em>what's between the numbers?</em>
          </p>
        </Prose>

        <MTQuote>
          The body, when it feels that consistent pulse, it just wants to move. And movement
          is a huge aspect of this art form, which we have to understand and use for our
          audience.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The three division systems" title="÷2, ÷3, ÷4 — and how each one feels">
        <Glossary>
          <Term name="Divide by 2 (eighth notes)" def="The default in pop, rock, EDM, classical. 'One AND two AND three AND four AND.' The body marches in even halves." />
          <Term name="Divide by 3 (triplets)" def="Waltzes, ballads, blues shuffles, gospel. 'One trip-let two trip-let.' The body sways or lilts." />
          <Term name="Divide by 4 (sixteenths)" def="Funk, R&B, drum-and-bass, hip-hop. 'One e and a two e and a.' The body shock-jumps in fine motion." />
        </Glossary>

        <NoteImage
          src="/notes/ear-training/finals/03a-rhythmic-flowchart.png"
          caption="Jason's rhythmic flowchart: Listen to the Song → Feel the Pulse → Time Signature & Beat Awareness → Time Feel (÷2 / ÷3 / ÷4 + straight or swing) → Rhythmic Devices. We're at the Time Feel step here."
        />
      </Chapter>

      <Chapter eyebrow="The body test" title="What is your head actually doing?">
        <Prose>
          <p>
            Before counting, observe your body. Do you nod evenly (÷2 or ÷4) or sway side
            to side (÷3)? Even vs. swaying is the first fork in the decision tree. Even
            means you're in a duple or quadruple division. Swaying means triplets.
          </p>
          <p>
            From there, listen to whether the off-beat lands halfway between numbers
            (straight) or two-thirds of the way through (swing). Same maths, different
            feel. Most jazz, blues, and shuffle grooves are swung. Most pop is straight.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Practice" title="The three-song drill">
        <Prose>
          <p>
            Pick three songs that feel obviously different rhythmically. Play each, find
            the pulse, then ask: am I hearing 2, 3, or 4 events between numbers? Within
            five songs you'll find your ear locks onto the answer in seconds. Within twenty,
            it becomes automatic.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/ear-training-4"
        label="Chapter 4 · How to Identify Intervals (by Ear)"
        why="Now we get to the centrepiece of ear training — the twelve intervals and their distinct emotional fingerprints. Resolution, anticipation, mystery, tension."
      />
    </LessonShell>
  );
}
